
import { Link } from 'react-router-dom';

interface InvestmentCardProps {
  investment: {
    id: number | string;
    title: string;
    location: string;
    targetReturn?: string;
    roi?: string;
    minInvestment?: number;
    investment?: string;
    timeline?: string;
    image: string;
    description: string;
    currentRaised?: number;
    totalRaise?: number;
    highlights?: string[];
    features?: string[];
    type?: string;
    status?: string;
  };
}

export default function InvestmentCard({ investment }: InvestmentCardProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Handle different data structures with proper fallbacks
  const displayFeatures = (investment.highlights || investment.features || []).filter(Boolean);
  const displayROI = investment.targetReturn || investment.roi || 'N/A';
  const displayMinInvestment = investment.minInvestment ? formatCurrency(investment.minInvestment) : investment.investment || 'Contact for details';
  const progressPercentage = investment.currentRaised && investment.totalRaise 
    ? Math.min((investment.currentRaised / investment.totalRaise) * 100, 100) 
    : 0;

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 group">
      <div className="relative h-64 overflow-hidden">
        <img 
          src={investment.image} 
          alt={investment.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-4 left-4 bg-emerald-600 text-white px-3 py-1 rounded-full text-sm font-medium">
          {displayROI} ROI
        </div>
        {investment.status && (
          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium capitalize">
            {investment.status}
          </div>
        )}
      </div>
      
      <div className="p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xl font-bold text-gray-900">{investment.title}</h3>
          <span className="text-emerald-600 font-semibold text-lg">{displayROI}</span>
        </div>
        
        <div className="flex items-center text-gray-600 mb-3">
          <i className="ri-map-pin-line text-emerald-600 mr-2"></i>
          <span className="text-sm">{investment.location}</span>
        </div>

        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{investment.description}</p>

        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <span className="text-gray-500">Min Investment:</span>
            <div className="font-semibold text-gray-900">{displayMinInvestment}</div>
          </div>
          {investment.timeline && (
            <div>
              <span className="text-gray-500">Timeline:</span>
              <div className="font-semibold text-gray-900">{investment.timeline}</div>
            </div>
          )}
        </div>

        {investment.currentRaised && investment.totalRaise && (
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Funding Progress</span>
              <span>{Math.round(progressPercentage)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{formatCurrency(investment.currentRaised)} raised</span>
              <span>{formatCurrency(investment.totalRaise)} goal</span>
            </div>
          </div>
        )}

        {displayFeatures.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {displayFeatures.slice(0, 2).map((feature, index) => (
              <span key={index} className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full text-xs">
                {feature}
              </span>
            ))}
            {displayFeatures.length > 2 && (
              <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                +{displayFeatures.length - 2} more
              </span>
            )}
          </div>
        )}

        <div className="flex space-x-3">
          <Link 
            to={`/investment-detail/${investment.id}`}
            className="flex-1 bg-emerald-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-emerald-700 transition-colors text-center cursor-pointer whitespace-nowrap"
          >
            View Details
          </Link>
          <Link 
            to="/investment-packages"
            className="flex-1 border border-emerald-600 text-emerald-600 py-2 px-4 rounded-lg font-semibold hover:bg-emerald-50 transition-colors text-center cursor-pointer whitespace-nowrap"
          >
            Invest Now
          </Link>
        </div>

      </div>
    </div>
  );
}
